import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { track } from '@/lib/analytics';
import {
  FiArrowLeft,
  FiBell,
  FiCheckCircle,
  FiUserPlus,
  FiMessageCircle,
  FiFileText,
  FiDollarSign,
  FiCalendar,
  FiInfo,
  FiAlertCircle,
} from 'react-icons/fi';
import { FaGavel } from 'react-icons/fa';
import { Card } from '@/components';
import { apiJson } from '@/lib/api';
import type { NotificationListResponse, Notification } from '@/lib/types';

// Format notification time matching mobile app logic
function formatNotificationTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isYesterday) {
    return 'Yesterday';
  }

  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// Icon component map for notification types
const notificationIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  coparent_joined: FiUserPlus,
  message_sent: FiMessageCircle,
  expense_created: FiFileText,
  payment_created: FiDollarSign,
  expense_dispute_created: FaGavel,
  payment_dispute_created: FaGavel,
  custody_exception_created: FiCalendar,
  custody_exception_updated: FiCalendar,
  system: FiInfo,
};

// Color map for notification types
const notificationColors: Record<string, string> = {
  coparent_joined: '#10B981', // green
  message_sent: '#018B9C', // primary
  expense_created: '#F59E0B', // amber
  payment_created: '#8B5CF6', // purple
  expense_dispute_created: '#EF4444', // red
  payment_dispute_created: '#EF4444', // red
  custody_exception_created: '#3B82F6', // blue
  custody_exception_updated: '#3B82F6', // blue
  system: '#6B7280', // gray
};

function getNotificationIcon(notificationType: string) {
  return notificationIcons[notificationType] || FiBell;
}

function getNotificationColor(notificationType: string): string {
  return notificationColors[notificationType] || '#018B9C';
}

function NotificationsPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Track screen view
  useEffect(() => {
    track('Notifications Screen');
  }, []);

  // Fetch notifications
  const notificationsQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiJson<NotificationListResponse>(getToken, '/v1/notifications'),
  });

  // Mark notifications as read mutation
  const markReadMutation = useMutation({
    mutationFn: async (notificationIds?: number[]) => {
      return apiJson(getToken, '/v1/notifications/read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_ids: notificationIds ?? null }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const notifications = notificationsQuery.data?.notifications ?? [];
  const unreadCount = notificationsQuery.data?.unread_count ?? 0;

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already
    if (!notification.read_at) {
      markReadMutation.mutate([notification.id]);
    }

    // Navigate to deeplink if available
    if (notification.deeplink) {
      // Convert mobile deeplinks to web routes
      // Mobile deeplinks may be like "/expenses/123" or "/(app)/expenses/123"
      let webRoute = notification.deeplink;
      // Remove mobile-specific path segments
      webRoute = webRoute.replace(/\/\(app\)/g, '/app');
      webRoute = webRoute.replace(/\/\(tabs\)/g, '');
      // Ensure it starts with /app if it's a relative path
      if (!webRoute.startsWith('/app') && !webRoute.startsWith('http')) {
        webRoute = `/app${webRoute.startsWith('/') ? '' : '/'}${webRoute}`;
      }
      navigate({ to: webRoute });
    }
  };

  const handleMarkAllRead = () => {
    if (unreadCount > 0) {
      markReadMutation.mutate(undefined);
    }
  };

  const isLoading = notificationsQuery.isLoading;
  const isError = notificationsQuery.isError;

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Mobile Header - hidden on desktop */}
      <header className='lg:hidden bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4'>
          <div className='flex items-center justify-between'>
            <Link
              to='/app/expenses'
              className='p-2 hover:bg-gray-100 rounded-full transition-colors -ml-2'
            >
              <FiArrowLeft className='w-5 h-5 text-primary' />
            </Link>
            <h1
              className='text-2xl font-light text-primary'
              style={{
                fontFamily:
                  "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Notifications
            </h1>
            {unreadCount > 0 ? (
              <button
                onClick={handleMarkAllRead}
                disabled={markReadMutation.isPending}
                className='p-2 hover:bg-gray-100 rounded-full transition-colors -mr-2'
              >
                <FiCheckCircle className='w-5 h-5 text-primary' />
              </button>
            ) : (
              <div className='w-9' />
            )}
          </div>
        </div>
      </header>

      {/* Desktop Header - shown only on large screens */}
      <header className='hidden lg:block bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-8 py-4'>
          <div className='flex items-center justify-between'>
            <h1
              className='text-xl font-semibold text-gray-900'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Notifications
            </h1>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={markReadMutation.isPending}
                className='flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-xl transition-colors font-semibold'
              >
                <FiCheckCircle className='w-4 h-4' />
                Mark all read
              </button>
            )}
          </div>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6 space-y-3'>
        {isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
          </div>
        ) : isError ? (
          <div className='text-center py-16'>
            <FiAlertCircle className='w-12 h-12 text-gray-400 mx-auto mb-4' />
            <p
              className='text-lg font-bold text-gray-900 mb-2'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Couldn't load notifications
            </p>
            <p className='text-gray-500 mb-4'>Pull down to try again</p>
            <button
              onClick={() => notificationsQuery.refetch()}
              className='px-6 py-2 bg-primary text-white rounded-xl font-semibold hover:bg-primary-hover transition-colors'
            >
              Retry
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className='text-center py-16'>
            <FiBell className='w-12 h-12 text-gray-400 mx-auto mb-4' />
            <p
              className='text-lg font-bold text-gray-900 mb-2'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              No notifications yet
            </p>
            <p className='text-gray-500'>
              You'll see updates about expenses, payments, and more here
            </p>
          </div>
        ) : (
          notifications.map((notification) => {
            const isUnread = !notification.read_at;
            const IconComponent = getNotificationIcon(notification.notification_type);
            const iconColor = getNotificationColor(notification.notification_type);

            return (
              <Card
                key={notification.id}
                className={`flex items-start gap-3 cursor-pointer hover:shadow-md transition-shadow ${
                  isUnread ? 'bg-primary/5 border-primary/20' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div
                  className='w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0'
                  style={{ backgroundColor: `${iconColor}15`, color: iconColor }}
                >
                  <IconComponent className='w-5 h-5' />
                </div>
                <div className='flex-1 min-w-0'>
                  <p
                    className={`font-bold truncate ${
                      isUnread ? 'text-gray-900' : 'text-gray-600'
                    }`}
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    }}
                  >
                    {notification.title}
                  </p>
                  <p
                    className='text-sm text-gray-500 line-clamp-2 mt-1'
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    }}
                  >
                    {notification.body}
                  </p>
                  <p className='text-xs text-gray-400 mt-2'>
                    {formatNotificationTime(notification.created_at)}
                  </p>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/notifications')({
  component: NotificationsPage,
});
